import { API_URLS } from '../config/api';
import {
  LoginRequest,
  LoginResponse,
  MeResponse,
  ProfileResponse,
  UpdateProfileRequest,
} from '../types/auth';

// Auth API client (contracts.md §8.1). The Bearer header is attached globally
// by the fetch interceptor in authToken.ts, so calls here only set JSON headers.

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.error || parsed.detail || parsed.title || message;
    } catch {
      if (errorText) message = errorText;
    }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

const jsonHeaders = { 'Content-Type': 'application/json' };

class AuthAPIService {
  async login(request: LoginRequest): Promise<LoginResponse> {
    const response = await fetch(API_URLS.AUTH_LOGIN, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(request),
    });
    return handleResponse<LoginResponse>(response);
  }

  async logout(): Promise<void> {
    try {
      const response = await fetch(API_URLS.AUTH_LOGOUT, { method: 'POST', headers: jsonHeaders });
      await handleResponse<void>(response);
    } catch {
      // Logout is best-effort (stateless JWT); the client discards the token regardless.
    }
  }

  async me(): Promise<MeResponse> {
    const response = await fetch(API_URLS.AUTH_ME, { method: 'GET', headers: jsonHeaders });
    return handleResponse<MeResponse>(response);
  }

  async getProfile(): Promise<ProfileResponse> {
    const response = await fetch(API_URLS.AUTH_ME_PROFILE, { method: 'GET', headers: jsonHeaders });
    return handleResponse<ProfileResponse>(response);
  }

  async updateProfile(request: UpdateProfileRequest): Promise<ProfileResponse> {
    const response = await fetch(API_URLS.AUTH_ME_PROFILE, {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(request),
    });
    return handleResponse<ProfileResponse>(response);
  }
}

export const authAPI = new AuthAPIService();
export default authAPI;
